import * as React from "react";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/**
 * localStorage key. Kept in sync with the inline no-flash script in `apps/web/index.html` —
 * if you rename this, update that script too.
 */
export const THEME_STORAGE_KEY = "academia-hub-theme";

interface ThemeContextValue {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

/**
 * Subscribes to the OS color-scheme preference via `useSyncExternalStore` — the React-endorsed
 * way to read a genuinely external, mutable source, rather than mirroring it into `useState` and
 * updating it from inside an effect (which the `react-hooks/set-state-in-effect` rule correctly
 * flags as an avoidable extra render).
 */
function subscribeToSystemScheme(onChange: () => void): () => void {
  const media = window.matchMedia(DARK_MEDIA_QUERY);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}

function readSystemPrefersDark(): boolean {
  return window.matchMedia(DARK_MEDIA_QUERY).matches;
}

function readSystemPrefersDarkServer(): boolean {
  return false;
}

function readStoredPreference(): ThemePreference {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // localStorage unavailable (private mode / blocked) — fall through to the light default.
  }
  // 17_UI_VISUAL_DESIGN_SYSTEM.md §4: default is light, never the OS preference, on first run.
  return "light";
}

export interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [preference, setPreferenceState] = React.useState<ThemePreference>(readStoredPreference);

  const systemPrefersDark = React.useSyncExternalStore(subscribeToSystemScheme, readSystemPrefersDark, readSystemPrefersDarkServer);

  const resolvedTheme: ResolvedTheme = preference === "system" ? (systemPrefersDark ? "dark" : "light") : preference;

  // Legitimate effect use per React's own guidance: synchronizing React's computed state with an
  // external system (the DOM attribute our CSS theme variant selector reads), not deriving state.
  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", resolvedTheme);
  }, [resolvedTheme]);

  const setPreference = React.useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Ignore write failures; theme still applies for this session.
    }
  }, []);

  const value = React.useMemo(() => ({ preference, resolvedTheme, setPreference }), [preference, resolvedTheme, setPreference]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = React.useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within a ThemeProvider");
  return context;
}
